import React from 'react';

const Llama4OpenSource = () => (
  <article className="news-article-body">
    <p className="news-lead">
      April 2025 marked a pivotal moment in open-source AI: Meta released
      <strong> Llama 4</strong> — a natively multimodal model family with a
      10-million-token context window, MoE architecture, and benchmark performance
      that matches or exceeds GPT-4o and Gemini 2.0 Flash. Combined with Qwen3.5,
      Mistral Small 4, and Kimi K2.5, the 2025–2026 open-source model wave has
      fundamentally closed the gap with proprietary frontier models.
    </p>

    <h2>Meta Llama 4 — April 5, 2025</h2>
    <p>
      Llama 4 is Meta's most ambitious open-source release: the first natively multimodal
      Llama model, built on a Mixture-of-Experts architecture and trained on a
      <strong>10-million-token context window</strong> — the largest context of any
      open-weights model at launch.
    </p>

    <h3>The Llama 4 Family</h3>
    <div className="news-timeline">
      <div className="news-timeline-item">
        <span className="news-timeline-date">Llama 4 Scout</span>
        <div>
          <strong>17B active parameters, 16 experts.</strong> Fits on a single NVIDIA H100 GPU.
          10M token context window. Best multimodal model in its class — outperforming Google
          Gemma 3, Gemini 2.0 Flash-Lite, and Mistral 3.1 on key benchmarks.
          Available for download on llama.com and HuggingFace.
        </div>
      </div>
      <div className="news-timeline-item">
        <span className="news-timeline-date">Llama 4 Maverick</span>
        <div>
          <strong>17B active parameters, 128 experts.</strong> Best multimodal performance
          in class — beats GPT-4o and Gemini 2.0 Flash across benchmarks. Achieves results
          comparable to DeepSeek V3 on reasoning and coding with less than half the active
          parameters. Available for download.
        </div>
      </div>
      <div className="news-timeline-item">
        <span className="news-timeline-date">Llama 4 Behemoth (in training)</span>
        <div>
          <strong>288B active parameters.</strong> Serves as a teacher model for Scout and
          Maverick via distillation. Already outperforms GPT-4.5, Claude Sonnet 3.7, and
          Gemini 2.0 Pro on STEM benchmarks. Not yet released for download.
        </div>
      </div>
      <div className="news-timeline-item">
        <span className="news-timeline-date">Llama 4 Reasoning (in development)</span>
        <div>
          Specialised reasoning model in the Llama 4 family — extending the series into
          the chain-of-thought and extended thinking capability space. No release date
          confirmed as of April 2026.
        </div>
      </div>
    </div>

    <h2>Why the MoE Architecture Matters</h2>
    <p>
      Both Scout and Maverick use <strong>Mixture-of-Experts (MoE)</strong> — a design
      where only a subset of the model's parameters are active for any given token.
      Scout activates 17B of its total parameters; Maverick activates 17B of a much
      larger pool via 128 experts. The result: you get the reasoning quality of a
      much larger model at the inference cost of a smaller one.
    </p>
    <p>
      Critically, Scout fits on a single H100 GPU — meaning any organisation with a
      single data-centre-grade GPU can run a frontier-class multimodal model locally,
      with no cloud dependency.
    </p>

    <h2>The Open-Source Model Wave: A Summary</h2>
    <p>
      Llama 4 is the capstone of an extraordinary 18-month surge in open-source
      frontier AI. Across the ecosystem:
    </p>
    <div className="news-specs-grid">
      <div className="news-spec-item">
        <span className="news-spec-label">Meta Llama 4 Maverick</span>
        <span className="news-spec-value">17B active / 128 experts · 10M context · Beats GPT-4o</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Alibaba Qwen3.5-397B</span>
        <span className="news-spec-value">397B total / 17B active · 201 languages · Vision + video</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Mistral Small 4</span>
        <span className="news-spec-value">119B total / 6B active · 256K context · Multimodal</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">Kimi K2.5</span>
        <span className="news-spec-value">1T total / 32B active · 256K context · Agent Swarm</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">DeepSeek R1</span>
        <span className="news-spec-value">Open weights · Matches o1 reasoning · $5.6M training cost</span>
      </div>
      <div className="news-spec-item">
        <span className="news-spec-label">MiniMax M2.7</span>
        <span className="news-spec-value">Self-evolving · 30–50% RL workflow automation</span>
      </div>
    </div>

    <h2>What This Means for the Proprietary AI Business</h2>
    <p>
      The open-source model wave directly compresses the value proposition of closed
      proprietary API providers. When a model running on a single H100 (Llama 4 Scout)
      matches GPT-4o performance, the premium for API access shrinks to:
    </p>
    <ul>
      <li><strong>Convenience</strong>: Managed API vs self-hosting infrastructure</li>
      <li><strong>Scale</strong>: Elastic capacity without managing hardware</li>
      <li><strong>Safety</strong>: Constitutional AI, RLHF, and content filtering layers</li>
      <li><strong>Frontier exclusivity</strong>: The very latest model releases, before open-source alternatives emerge</li>
    </ul>
    <p>
      The window of frontier exclusivity — where only proprietary models achieve state-of-the-art
      — has shrunk from years to months. The open-source ecosystem is now within one generation
      of matching any closed model, typically within 6–12 months of a new proprietary release.
    </p>

    <h2>Licence Nuances</h2>
    <p>
      Not all "open-source" AI models are equally open:
    </p>
    <ul>
      <li><strong>Llama 4</strong>: Meta's custom Llama licence — commercial use allowed above 700M MAU requires separate agreement</li>
      <li><strong>Qwen3.5</strong>: Apache 2.0 — fully open, commercial use permitted</li>
      <li><strong>Mistral models</strong>: Primarily Apache 2.0, some under Mistral Research Licence</li>
      <li><strong>DeepSeek R1</strong>: MIT licence — maximum openness</li>
      <li><strong>Kimi K2.5</strong>: Open weights with usage terms — check current licence on HuggingFace</li>
    </ul>
    <p>
      For enterprises, the licence choice matters as much as the model's benchmark scores —
      Apache 2.0 and MIT provide the cleanest commercial usage rights.
    </p>

    <div className="news-insight-box">
      <h3>Key Takeaway</h3>
      <p>
        The open-source AI model wave of 2025–2026 has permanently altered the competitive
        landscape. Any organisation willing to operate its own inference infrastructure
        now has access to frontier-class models — multimodal, long-context, capable of
        autonomous reasoning — without per-token API costs or data sharing with third
        parties. The question for enterprise AI strategy is no longer "should we use
        open-source models?" but "which open-source models match our specific workloads
        and compliance requirements?"
      </p>
    </div>
  </article>
);

export default Llama4OpenSource;
